import type {
  PipelineHistoryField,
  PipelineHistoryFieldChange,
} from '@repo/lakehouse-sdk';

import { normalizeHistoryColumns } from './historyColumns';

export interface HistoryNode {
  path: string;
  key: string;
  label?: string;
  field?: PipelineHistoryField;
  children: HistoryNode[];
}

const LABELS: Record<string, string> = {
  native_stream_properties: 'Native stream',
  nativeStreamProperties: 'Native stream',
  object_store: 'Source',
  bucket_files: 'Source files',
  kafka: 'Kafka source',
  iceberg: 'Iceberg destination',
  paimon: 'Paimon destination',
  transform: 'Transforms',
  upsert: 'Upserts',
  stats: 'Statistics',
  sampling: 'Sampling',
  tags: 'Tags',
  compute_size: 'Compute size',
  max_parallelism: 'Maximum parallelism',
  initial_tm_count: 'Initial workers',
  max_tm_count: 'Maximum workers',
  slots_per_tm: 'Slots per worker',
  rate: 'Keep rate',
  holdoutRate: 'Holdout rate',
  dryRun: 'Dry run',
  inputFilter: 'Input filter',
  subjectWhen: 'Apply when',
  expr: 'Expression',
  primaryKey: 'Primary key',
  sequenceColumn: 'Sequence column',
  deleteWhen: 'Delete condition',
};

export const historyLabel = (key: string): string =>
  (Object.hasOwn(LABELS, key) ? LABELS[key] : undefined) ??
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .replace(/^./, (letter) => letter.toUpperCase());

const pathSegments = (path: string): string[] =>
  path === ''
    ? []
    : path
        .slice(1)
        .split('/')
        .map((part) => part.replaceAll('~1', '/').replaceAll('~0', '~'));

const isRuntime = (key: string): boolean =>
  key === 'native_stream_properties' || key === 'nativeStreamProperties';

const COMPUTE_FIELDS = new Set([
  'compute_size',
  'max_parallelism',
  'slots_per_tm',
  'initial_tm_count',
  'max_tm_count',
]);

const sectionForPath = (path: string): string => {
  const [first = '', second] = pathSegments(path);
  if (isRuntime(first)) {
    return second ? historyLabel(second) : 'Native stream';
  }
  if (COMPUTE_FIELDS.has(first)) {
    return 'Compute';
  }
  if (
    [
      'object_store',
      'bucket_files',
      'kafka',
      'iceberg',
      'paimon',
      'tags',
    ].includes(first)
  ) {
    return historyLabel(first);
  }
  return 'General';
};

export const historyPathLabel = (path: string): string => {
  const parts = pathSegments(path);
  return (
    parts
      .map((part, index) =>
        /^\d+$/.test(part)
          ? `[${part}]`
          : index > 0 && parts[index - 1] === 'tags'
            ? part
            : historyLabel(part)
      )
      .join(' › ') || 'Configuration'
  );
};

export const historyValueText = (
  field: PipelineHistoryField | undefined
): string => {
  if (!field) {
    return '--';
  }
  switch (field.kind) {
    case 'NULL':
      return 'null';
    case 'ARRAY':
      return '[]';
    case 'OBJECT':
      return '{}';
    case 'STRING':
      return field.value === undefined || field.value === null
        ? '--'
        : JSON.stringify(field.value);
    default:
      return field.value ?? '--';
  }
};

export const historyChangeKind = (
  change: PipelineHistoryFieldChange
): 'Added' | 'Removed' | 'Modified' =>
  !change.before ? 'Added' : !change.after ? 'Removed' : 'Modified';

export const changesView = <T extends { path: string }>(
  changes: T[]
): { title: string; changes: T[] }[] => {
  const groups = new Map<string, T[]>();
  for (const change of changes) {
    const title = sectionForPath(change.path);
    const group = groups.get(title) ?? [];
    group.push(change);
    groups.set(title, group);
  }
  return Array.from(groups, ([title, changes]) => ({ title, changes }));
};

export const snapshotView = (fields: PipelineHistoryField[]): HistoryNode[] => {
  const normalized = normalizeHistoryColumns([fields]);
  fields = normalized.snapshots[0];
  const root: HistoryNode = { path: '', key: 'Configuration', children: [] };
  const nodes = new Map<string, HistoryNode>([['', root]]);
  for (const field of fields) {
    let parent = root;
    let path = '';
    for (const key of pathSegments(field.path)) {
      path += `/${key.replaceAll('~', '~0').replaceAll('/', '~1')}`;
      let node = nodes.get(path);
      if (!node) {
        node = { path, key, label: normalized.names.get(path), children: [] };
        nodes.set(path, node);
        parent.children.push(node);
      }
      parent = node;
    }
    parent.field = field;
  }
  for (const node of nodes.values()) {
    if (node.field?.kind === 'ARRAY') {
      node.children.sort((a, b) =>
        a.label && b.label
          ? a.label.localeCompare(b.label)
          : Number(a.key) - Number(b.key)
      );
    }
  }
  return (root.field && root.field.kind !== 'OBJECT') || !root.children.length
    ? [root]
    : root.children;
};

const containsNonNullValue = (node: HistoryNode): boolean =>
  node.children.length
    ? node.children.some(containsNonNullValue)
    : node.field?.kind !== 'NULL';

export const snapshotSections = (
  fields: PipelineHistoryField[]
): { title: string; nodes: HistoryNode[] }[] => {
  const groups = new Map<string, HistoryNode[]>();
  for (const node of snapshotView(fields)) {
    const entries =
      isRuntime(node.key) && node.children.length ? node.children : [node];
    for (const entry of entries) {
      const title = sectionForPath(entry.path);
      const group = groups.get(title) ?? [];
      group.push(entry);
      groups.set(title, group);
    }
  }
  return Array.from(groups, ([title, nodes]) => ({ title, nodes })).filter(
    (section) => section.nodes.some(containsNonNullValue)
  );
};
